import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Code, Briefcase, Handshake, Scale, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CareersPage = () => {
  const navigate = useNavigate();

  const interestAreas = [
    {
      icon: Code,
      title: 'Product & Platform Development',
      description: 'Building and improving the core SnowExchange platform experience.'
    },
    {
      icon: Code,
      title: 'Engineering & AI',
      description: 'Developing intelligent features, algorithms, and technical infrastructure.'
    },
    {
      icon: Briefcase,
      title: 'Business Development & Sales',
      description: 'Growing our user base and building relationships with partners.'
    },
    {
      icon: Handshake,
      title: 'Partnerships & Ecosystem Development',
      description: 'Expanding our network within the ServiceNow ecosystem.'
    },
    {
      icon: Scale,
      title: 'Legal, Compliance & Operations',
      description: 'Ensuring we operate with integrity and efficiency.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-600" />
            <span className="font-semibold text-gray-800">Careers</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          {/* Title */}
          <div className="text-center mb-12 pb-8 border-b border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
              <Users className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Careers at SnowExchange</h1>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Join us in building the modern infrastructure for ServiceNow staffing and delivery.
            </p>
          </div>

          {/* About Section */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">About SnowExchange</h2>
            <p className="text-gray-700 leading-relaxed">
              SnowExchange is an early-stage B2B platform designed to facilitate professional collaboration within the ServiceNow ecosystem. We connect consulting firms, partners, freelancers, and clients through a modern, neutral operating layer that brings visibility, coordination, and trust to resource allocation and project delivery.
            </p>
          </section>

          {/* Current Status */}
          <section className="mb-10">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
              <h2 className="text-lg font-semibold text-amber-800 mb-2">Current Hiring Status</h2>
              <p className="text-amber-700">
                We are currently in an early-stage phase and do not have active hiring campaigns at this time. However, we are always open to hearing from talented individuals who share our vision and want to be part of what we're building.
              </p>
            </div>
          </section>

          {/* Interest Areas */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Areas of Interest</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              While we're not actively recruiting, we're always interested in connecting with professionals in the following areas (non-exhaustive):
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {interestAreas.map((area, index) => (
                <div 
                  key={index} 
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-teal-300 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <area.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{area.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{area.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Spontaneous Applications */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Spontaneous Applications</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you're passionate about the ServiceNow ecosystem, B2B platforms, or the future of professional collaboration, we'd love to hear from you. Send us your profile and tell us how you could contribute to SnowExchange.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 rounded-full mb-3">
                <Mail className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-gray-600 mb-2">Send your application to:</p>
              <a 
                href="mailto:careers@snow-exchange.com" 
                className="text-xl font-semibold text-teal-600 hover:text-teal-700 transition-colors"
              >
                careers@snow-exchange.com
              </a>
            </div>
          </section>

          {/* Values */}
          <section className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">What We Value</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <div className="text-2xl mb-2">🎯</div>
                <h3 className="font-medium text-gray-900 mb-1">Practicality</h3>
                <p className="text-sm text-gray-500">Real-world solutions over theoretical perfection.</p>
              </div>
              <div className="text-center p-4">
                <div className="text-2xl mb-2">🤝</div>
                <h3 className="font-medium text-gray-900 mb-1">Collaboration</h3>
                <p className="text-sm text-gray-500">Working together beats working alone.</p>
              </div>
              <div className="text-center p-4">
                <div className="text-2xl mb-2">🌱</div>
                <h3 className="font-medium text-gray-900 mb-1">Sustainability</h3>
                <p className="text-sm text-gray-500">Building something that lasts.</p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 mt-12 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} SnowExchange. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CareersPage;
